import { AGENCY_CARRIERS, findCarrier, isCrm } from "@iqa/contracts/carriers";
import { type CurrentPolicy, currentPolicyOf } from "@iqa/contracts/quote-values";
import type { Client, Property, Quote, QuoteEntry, QuoteRun } from "./api.ts";

/**
 * What the quote and client pages show of the data, worked out once: a client's name, an address
 * on one line, a quote's runs at carriers apart from its runs in a CRM, where each carrier is
 * signed in to, its best premium and what it saves against the client's current policy (the
 * property's `priorPremium`, or the one the quote was priced against from their loan:
 * docs/carrier-runs.md#preliminary-and-final-quotes).
 */

export const quoteLink = (quoteId: string) => `#/quotes/${encodeURIComponent(quoteId)}`;
export const clientLink = (clientId: string) => `#/clients/${encodeURIComponent(clientId)}`;

/** A business by its name, a person by first and last name, else by the contact's name. */
export function clientName(client: Client): string {
  const person = [client.firstName, client.lastName].filter(Boolean).join(" ");
  return client.businessName || person || client.contactName || "Unnamed client";
}

/** `1 Main St, Kansas City, MO 64111`, or an empty string without an address. */
export function addressLine(property: Property): string {
  const address = property.address;
  if (!address) return "";
  const street = [address.street, address.unit].filter(Boolean).join(" ");
  const region = [address.state, address.zip].filter(Boolean).join(" ");
  return [street, address.city, region].filter(Boolean).join(", ");
}

/** A quote without a stage is final. */
export function stageOf(quote: Quote): "preliminary" | "final" {
  return quote.stage ?? "final";
}

/** The quoted run with the lowest premium; none while no carrier gave one. */
export function bestRun(runs: readonly QuoteRun[]): QuoteRun | undefined {
  let best: QuoteRun | undefined;
  for (const run of runs) {
    if (run.status !== "quoted" || run.premium === undefined) continue;
    if (!best || run.premium < (best.premium as number)) best = run;
  }
  return best;
}

/** The runs at insurance carriers: a run in a CRM (Arive, LendingPad) read the loan, priced nothing. */
export function carrierRuns(runs: readonly QuoteRun[]): QuoteRun[] {
  return runs.filter((run) => run.carrier === undefined || !isCrm(run.carrier));
}

/** The runs in a CRM, where the client's loan was read. */
export function crmRuns(runs: readonly QuoteRun[]): QuoteRun[] {
  return runs.filter((run) => run.carrier !== undefined && isCrm(run.carrier));
}

/** An http(s) URL as it is, or nothing: a run's last URL is the portal's, never shown otherwise. */
function webUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Where a run's carrier is signed in to: its portal's start page from the carrier directory, else
 * the site the run was on. A carrier keeps the quote in the signed-in session (National General's
 * pages have no quote in their URL), so the producer finds it there by its quote number.
 */
export function carrierPortalUrl(run: QuoteRun): string | undefined {
  const known = run.carrier === undefined ? undefined : findCarrier(AGENCY_CARRIERS, run.carrier);
  if (known) return known.portalUrl;
  const last = webUrl(run.lastUrl);
  return last && new URL(last).origin;
}

/** A CRM run's last page: the loan, once the producer is signed in to the CRM. */
export function loanUrl(run: QuoteRun): string | undefined {
  return webUrl(run.lastUrl);
}

/** What the client pays now: a property's confirmed policy, else the one the quote was priced against. */
export function currentPolicy(
  quote: Quote | undefined,
  properties: readonly Property[],
): CurrentPolicy | undefined {
  return currentPolicyOf(quote, properties);
}

export interface Savings {
  /** A year's premium saved; negative when the new one costs more. */
  readonly amount: number;
  /** Of the current premium, rounded to a whole percent. */
  readonly percent: number;
}

export function savingsOf(current: number | undefined, premium: number | undefined) {
  if (current === undefined || premium === undefined || current <= 0) return undefined;
  const amount = current - premium;
  return { amount, percent: Math.round((amount / current) * 100) } satisfies Savings;
}

/** A quote's best premium and what it saves, for the list and the quote's page. */
export function bestOffer(entry: QuoteEntry) {
  const run = bestRun(carrierRuns(entry.runs));
  const current = currentPolicy(entry.quote, entry.properties);
  return { run, current, savings: savingsOf(current?.premium, run?.premium) };
}
